/**
 * `B-09` et `B-10` — la file de revue et l'écran des 90 secondes.
 *
 * **C'est le seul écran du projet dont l'ergonomie a un coût salarial direct.**
 * Un analyste traite 300 dossiers par jour ; à ce rythme, une seconde perdue par
 * dossier coûte cinq minutes par jour, et la médiane visée — quatre-vingt-dix
 * secondes — décide si le poste est tenable. Trois conséquences gouvernent tout
 * ce fichier.
 *
 *  1. **Un seul aller-retour pour le DOSSIER.** Le serveur rend la pièce, le
 *     selfie, le geste attendu, les métadonnées, les comptes rapprochés et les
 *     motifs proposables en une fois. Six appels auraient coûté six attentes.
 *
 *     **Les images, elles, font deux appels de plus, et il n'y a pas moyen de
 *     faire autrement** : ce sont des octets, ils ne voyagent pas dans le JSON
 *     du dossier sans l'alourdir d'un tiers en base 64. Ils partent en même
 *     temps, chacun dans son cadre, et l'écran est utilisable avant qu'ils
 *     n'arrivent — le geste imposé, la pièce déclarée et les motifs sont déjà
 *     là. Ce qu'ils ne font PAS, c'est passer par le navigateur : un
 *     `<img src>` n'emporte aucun jeton, et une image servie sans jeton est une
 *     consultation que le serveur ne peut journaliser au nom de personne
 *     (`EX-BO-004`, `EX-DAT-007`).
 *  2. **Le clavier avant la souris.** La coquille `QueueShell` porte le
 *     parcours : Tab descend la file, Entrée ouvre, Échap referme, et une touche
 *     par décision. Après une décision, le focus va au dossier **suivant**.
 *  3. **Rien à taper.** Le motif se choisit dans une liste ; il n'y a aucun champ
 *     de texte sur cet écran, et c'est aussi ce qui rend le texte libre
 *     impossible.
 *
 * **Le motif de rejet n'est jamais du texte libre** (`EX-KYC-008`). Le client le
 * reçoit **traduit dans sa langue** et doit pouvoir corriger seul. Les boutons de
 * motif affichent donc le texte que **le client lira**, pas une étiquette
 * interne : l'analyste choisit ce qui va partir, et non un code dont il ne sait
 * pas ce qu'il produit.
 *
 * **Ce que la maquette porte, et ce qu'elle ne porte pas.** `B-10` est maquetté
 * — pièce et selfie côte à côte, outils d'image en haut à droite, colonne de
 * décision à droite avec ses motifs, rappel du geste sous les images. `B-09`,
 * la file, **n'a aucune maquette** : elle est composée ici depuis le système de
 * design et la coquille de file déjà livrée, sans composant ni motif
 * d'interaction nouveau. Les choix faits faute de maquette sont énumérés dans le
 * rapport du chantier, et ils reviennent au porteur (`UX-DR26`, `EX-UX-010`).
 *
 * **Les quatre outils d'image agissent réellement.** `zoomIn`, `zoomOut`,
 * `rotate` et `contrast` transforment les deux captures, et pas une vignette :
 * une pièce photographiée de travers dans une échoppe mal éclairée ne se juge
 * pas, et l'agent qui ne peut pas la redresser finit par approuver de guerre
 * lasse. Ils portent un **libellé**, là où la maquette dessine quatre icônes
 * nues de 30 × 30 : un outil qui décide de la lecture d'une pièce d'identité ne
 * peut pas se deviner, et un bouton sans nom n'est annonçable à personne.
 *
 * **La table de confrontation « déclaré / lu sur la pièce » n'est PAS rendue,
 * et l'écran le dit.** La maquette la dessine en six lignes, dont celle qui
 * fait tout son prix — « Lieu de naissance · Kaolack (déclaré : Kaolak) · À
 * vérifier ». `KycReviewMessages.CaseView` ne sert **aucune** valeur déclarée
 * par le client, ni aucun état de concordance : ni nom, ni date de naissance,
 * ni lieu de naissance, ni verdict de doublon. La fabriquer donnerait à lire
 * une concordance que personne n'a calculée — une assurance fabriquée par
 * l'écran, ce qui est pire que son absence. Ce qui est servi est rendu, sans
 * état ; le reste est **déclaré** sous le titre du dessin.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueDecision, QueueEntry } from '../shell/QueueShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import {
  assignKycCase,
  decideKycCase,
  fetchKycCase,
  fetchKycImage,
  fetchKycQueue,
} from '../http/kyc-review.js';
import type {
  KycCapture,
  KycCaseView,
  KycDecisionId,
  KycQueue,
  KycQueueRow,
  KycReason,
} from '../http/kyc-review.js';

/**
 * Le motif d'audit de la consultation.
 *
 * Le socle exige un motif sur toute lecture de données personnelles
 * (`EX-BO-004`) : c'est ce qui distingue un agent qui travaille d'un agent qui
 * fouille. Il est ici une **constante de l'écran** et non une saisie, parce que
 * la raison est toujours la même — l'analyste dépile sa file. Une invite par
 * dossier aurait coûté une frappe par dossier, soit, sur une journée, plusieurs
 * minutes de saisie pour une phrase identique.
 */
const MOTIF_DE_CONSULTATION = 'revue-kyc';

/**
 * Les colonnes de la file — **deux**, et c'est la maquette qui l'impose.
 *
 * L'artboard `b_revue` donne à la file `width:240px;flex:none`, et ses lignes ne
 * sont pas des lignes de table : c'est un **bouton** qui porte une colonne de
 * deux textes — un **nom** en 13 points, une ligne de contexte en 11 — et,
 * poussée à droite par `margin-left:auto`, une **étiquette**. Un nom, un
 * contexte, un tag : trois choses dans DEUX cellules.
 *
 * **Ce que l'étiquette porte, et ce qu'elle ne porte pas.** Le dessin y écrit
 * « Niveau 2 », « Niveau 1 » ou « Reprise » : un niveau six fois sur huit, un
 * état les deux autres. C'est donc le **niveau demandé** qui la remplit — et
 * « Reprise » reste dehors, parce que rien ne la sert : `compliance.kyc_case`
 * ne distingue pas un dossier redéposé après un complément d'un dossier neuf,
 * et l'inventer ferait porter à l'écran un jugement que personne n'a calculé.
 *
 * Elle ne porte plus le **compte d'alertes**, qui l'occupait. Le dessin met un
 * état dans son tag, pas un compte, et il n'y a qu'un tag : le compte a rejoint
 * le dossier ouvert, où il y a la place de l'écrire en toutes lettres.
 */
const colonnes = (fileEnCours: string): readonly QueueColumn[] => [
  // Le bandeau du dessin — « FILE D'ATTENTE » à gauche, le compte à droite —
  // est l'en-tête de la première colonne : le compte est SERVI, et une clé de
  // traduction ne peut pas le porter.
  { id: 'case', labelKey: 'identityReview.columns.case', headerLabel: fileEnCours },
  { id: 'level', labelKey: 'identityReview.columns.level' },
];

/**
 * Les trois décisions, avec les touches de la maquette : A, C, R.
 *
 * Elles sont déclarées ici et non dans la coquille : la coquille ne connaît
 * aucun métier, et c'est ce qui lui permet de servir aussi les alertes LBC/FT et
 * les écarts de réconciliation.
 */
const decisions: readonly QueueDecision[] = [
  { id: 'approve', labelKey: 'queue.approve', shortcut: 'a' },
  { id: 'complement', labelKey: 'queue.requestComplement', shortcut: 'c' },
  { id: 'reject', labelKey: 'queue.reject', shortcut: 'r' },
];

/** Les secondes rendues en minutes et secondes, sans dépendance de format. */
function duree(secondes: number): string {
  const minutes = Math.floor(secondes / 60);
  const reste = secondes % 60;
  return minutes === 0 ? `${reste} s` : `${minutes} min ${reste} s`;
}

/**
 * La même durée, **arrondie**, pour la ligne de 240 points.
 *
 * Le dessin écrit « 2 min », jamais « 2 min 14 s » : la seconde ligne d'une
 * entrée de file y tient sur UNE ligne, et c'est cette contrainte qui décide.
 * « 115 min 1 s » y prenait la place de la référence du compte, et l'entrée
 * passait à la ligne — le défaut même que ce chantier referme.
 *
 * La durée **exacte** n'est pas perdue : le dossier ouvert la porte en toutes
 * lettres, avec le verdict d'ancienneté. Arrondir dans la file et détailler dans
 * le dossier est la même conduite que partout ailleurs — la cellule est tronquée
 * par la largeur de sa colonne, le panneau ne l'est pas.
 */
function dureeCourte(secondes: number): string {
  const minutes = Math.floor(secondes / 60);
  if (minutes === 0) return `${secondes} s`;
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.floor(minutes / 60);
  return `${heures} h ${String(minutes % 60).padStart(2, '0')}`;
}

/** L'écran de revue : la file, et le dossier ouvert. */
export function IdentityReviewScreen(): ReactNode {
  const { t } = useTranslation();

  /**
   * **Le nom du client** — la première ligne d'une entrée, et ce qu'on lit
   * d'abord.
   *
   * C'est la seule chose qui distingue un dossier d'un autre. La file écrivait
   * « Niveau 2 · Libre-service » : huit lignes identiques, un niveau qui ne
   * nomme personne et une origine que six dossiers sur huit partagent.
   *
   * Les deux moitiés sont assemblées ICI et non par le serveur : « Prénom Nom »
   * est une mise en forme, et elle dépend de la langue de l'agent. Une moitié
   * manquante ne fait pas un demi-nom — les deux colonnes sont nullables, et un
   * compte ouvert au seul numéro de téléphone n'a pas encore de nom. L'absence
   * se **dit**, elle ne se remplace pas par un identifiant technique.
   */
  const nom = useCallback(
    (ligne: KycQueueRow): string => {
      const entier = [ligne.customerFirstName, ligne.customerLastName]
        .filter((moitie): moitie is string => moitie !== null && moitie.trim() !== '')
        .join(' ');
      return entier === '' ? t('identityReview.customerUnnamed') : entier;
    },
    [t],
  );

  /**
   * **La référence du COMPTE**, et non celle du dossier.
   *
   * Le dessin écrit « KYC-4021-88F3 » sous le nom. Cette référence-là n'existe
   * pas : `compliance.kyc_case` n'a pas de colonne de référence lisible,
   * seulement une clé primaire `UUID`, et l'inventer donnerait à l'analyste un
   * numéro que rien ne permet de retrouver. Celle du compte, elle, est réelle,
   * servie, et de la même taille — c'est celle qu'un client lit au téléphone.
   */
  const reference = useCallback(
    (ligne: KycQueueRow): string =>
      ligne.customerPublicRef ?? t('identityReview.customerUnknown'),
    [t],
  );

  /**
   * La seconde ligne : la référence du compte, puis l'ancienneté **arrondie**.
   *
   * **Le verdict d'ancienneté n'y est PAS écrit, et c'est un écart assumé.** Le
   * mot « vieillissant » y ajoute treize caractères, et la ligne ne tient plus
   * dans les 240 points du dessin : mesuré dans le navigateur, « 2X9C2SAZGP ·
   * 2 h 46 · vieillissant » demande 202 points pour une boîte qui en offre 127,
   * et l'ellipse emporte alors l'ANCIENNETÉ ELLE-MÊME — le fait le plus utile de
   * la ligne. Le dessin en dit autant : sa propre seconde ligne ne porte jamais
   * plus que « KYC-4021-88F3 · 2 min ».
   *
   * Le verdict passe donc par trois autres canaux, dont deux ne sont pas la
   * couleur : la **graisse** et la **couleur** de l'ancienneté (`data-ageing`),
   * le **libellé accessible** de la ligne, qui le nomme en toutes lettres, et le
   * **dossier ouvert**, qui écrit « 166 min 27 s — vieillissant » là où il y a
   * la place.
   */
  const contexte = useCallback(
    (ligne: KycQueueRow): string =>
      t('identityReview.queueMeta', {
        reference: reference(ligne),
        age: dureeCourte(ligne.ageSeconds),
      }),
    [reference, t],
  );

  /**
   * Le **libellé accessible** de la ligne : « Aminata Diallo — 3HXZLPC256 ».
   *
   * `aria-label` REMPLACE le contenu de la ligne : ce qu'on n'écrit pas ici, un
   * opérateur non voyant ne l'entend pas en parcourant la file. Il porte donc le
   * nom, la référence — et le verdict d'ancienneté, que la ligne visible ne dit
   * que par la couleur et la graisse.
   */
  const etiquetteDeLaLigne = useCallback(
    (ligne: KycQueueRow): string => {
      const titre = t('identityReview.caseTitle', {
        name: nom(ligne),
        reference: reference(ligne),
      });
      return ligne.ageing ? t('identityReview.ageingRowLabel', { title: titre }) : titre;
    },
    [nom, reference, t],
  );
  const client = useApiClient();

  const [file, setFile] = useState<KycQueue | null>(null);
  const [dossier, setDossier] = useState<KycCaseView | null>(null);
  const [motif, setMotif] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [contraste, setContraste] = useState(1);

  const charger = useCallback(async () => {
    try {
      setFile(await fetchKycQueue(client, MOTIF_DE_CONSULTATION));
      setErreur(null);
    } catch (echec) {
      setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /**
   * Ouvre un dossier : il s'affecte, puis il se lit.
   *
   * L'affectation d'abord, et c'est délibéré : un second analyste qui ouvre le
   * même dossier doit être **averti** avant d'avoir travaillé, pas après avoir
   * décidé. Le refus d'affectation n'empêche pas la lecture — un analyste a le
   * droit de regarder un dossier qu'un autre traite —, il l'avertit.
   */
  const ouvrir = useCallback(
    async (caseId: string) => {
      setMotif(null);
      setRotation(0);
      setZoom(1);
      setContraste(1);
      let avertissement: string | null = null;
      try {
        await assignKycCase(client, caseId, MOTIF_DE_CONSULTATION);
      } catch (echec) {
        // Un droit manquant n'est PAS un dossier déjà pris. L'auditeur, par exemple,
        // consulte la file sans pouvoir s'affecter quoi que ce soit : lui dire
        // « un autre analyste le traite » lui ferait attendre une place qui ne se
        // libérera jamais.
        avertissement =
          echec instanceof ApiError && echec.kind === 'forbidden'
            ? 'identityReview.assignForbidden'
            : 'identityReview.alreadyAssigned';
      }
      try {
        setDossier(await fetchKycCase(client, caseId, MOTIF_DE_CONSULTATION));
        setErreur(avertissement);
      } catch (echec) {
        setDossier(null);
        setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client],
  );

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (file?.rows ?? []).map((ligne) => ({
        id: ligne.caseId,
        // **Le libellé accessible de la ligne remplace son contenu**, et c'est
        // pourquoi il porte le nom. Il portait l'identifiant technique du
        // dossier : un opérateur non voyant entendait « Ouvrir le dossier
        // 7a33e8d5-4cb5-4adb-9f9b-c0627ed5c72e », huit fois de suite, et n'avait
        // aucun moyen de distinguer une ligne de la suivante. C'est le même
        // défaut que celui de l'écran, dans le canal qu'on regarde le moins.
        reference: etiquetteDeLaLigne(ligne),
        values: [
          // La vérité TEXTUELLE de la ligne — c'est elle que l'en-tête du
          // panneau lit, et le dessin y écrit « Ibrahima Diallo — KYC-4021-88F3 ».
          t('identityReview.caseTitle', {
            name: nom(ligne),
            reference: reference(ligne),
          }),
          t('identityReview.levelTag', { level: ligne.targetLevel }),
        ],
        // **La FORME du dessin** : un nom, puis son contexte sous lui, chacun
        // avec sa graisse et sa couleur. Deux éléments et non une chaîne à saut
        // de ligne : une chaîne n'a qu'un seul style, et son ellipse ne
        // s'applique qu'à sa dernière ligne — la première passerait à la ligne,
        // ce qui est exactement le défaut refermé ici.
        cells: [
          <span className="review__queue-entry" key={ligne.caseId}>
            <span className="review__queue-name">{nom(ligne)}</span>
            <span className="review__queue-meta" data-ageing={ligne.ageing ? 'true' : undefined}>
              {contexte(ligne)}
            </span>
          </span>,
          // **L'étiquette du dessin est une pastille**, et non du texte nu :
          // `padding:0 7px`, `border-radius:4px`, un fond. La couleur, elle,
          // reste NEUTRE — le dessin peint le niveau 2 en `warning` et le niveau
          // 1 en `accent`, ce qui met un jugement d'urgence sur un chiffre que
          // le serveur rend sans le porter. C'est la conduite déjà tenue par la
          // pastille du panneau de détail.
          <span className="review__queue-tag" key={`${ligne.caseId}-niveau`}>
            {t('identityReview.levelTag', { level: ligne.targetLevel })}
          </span>,
        ],
        // Le panneau, lui, n'est pas contraint par 240 points : il porte
        // l'ancienneté EXACTE, là où la file l'arrondit.
        fullValues: [
          t('identityReview.caseSummary', {
            name: nom(ligne),
            reference: reference(ligne),
            age: ligne.ageing
              ? t('identityReview.ageingAge', { age: duree(ligne.ageSeconds) })
              : duree(ligne.ageSeconds),
          }),
        ],
      })),
    [contexte, etiquetteDeLaLigne, file, nom, reference, t],
  );

  /** Les motifs proposables pour la décision demandée. */
  const motifsDe = useCallback(
    (decision: KycDecisionId): readonly KycReason[] => {
      if (dossier === null) return [];
      if (decision === 'approve') return dossier.approvalReasons;
      if (decision === 'reject') return dossier.rejectionReasons;
      return dossier.informationReasons;
    },
    [dossier],
  );

  const decider = useCallback(
    (entree: QueueEntry, decisionId: string) => {
      const decision = decisionId as KycDecisionId;
      const proposables = motifsDe(decision);
      // Le motif choisi doit appartenir à la liste de CETTE décision. Sans cette
      // vérification, un motif sélectionné pour un rejet partirait avec une
      // approbation, et le client lirait « votre pièce est illisible » sous
      // « dossier approuvé ».
      const retenu =
        motif !== null && proposables.some((candidat) => candidat.code === motif)
          ? motif
          : proposables.length === 1
            ? proposables[0].code
            : null;
      if (retenu === null) {
        setErreur('identityReview.reasonRequired');
        return;
      }
      void (async () => {
        try {
          await decideKycCase(client, {
            caseId: entree.id,
            decision,
            reasonCode: retenu,
            auditReason: MOTIF_DE_CONSULTATION,
          });
          setDossier(null);
          setMotif(null);
          setErreur(null);
          await charger();
        } catch (echec) {
          setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
        }
      })();
    },
    [charger, client, motif, motifsDe],
  );

  return (
    <section className="review" aria-label={t('identityReview.landmark')}>
      <p className="review__objective">
        {t('identityReview.objective', {
          target: duree(file?.targetSeconds ?? 0),
          count: file?.rows.length ?? 0,
        })}
      </p>
      {erreur === null ? null : (
        <p className="review__notice" role="alert">
          {t(erreur)}
        </p>
      )}

      <QueueShell
        columns={colonnes(
          t('identityReview.queueHeading', { count: file?.rows.length ?? 0 }),
        )}
        entries={lignes}
        decisions={decisions}
        onDecide={decider}
        // **Le dessin ne montre JAMAIS un côté droit vide.** L'écran livré
        // ouvrait sur une page blanche à côté d'une liste, et l'analyste devait
        // un geste de plus avant de commencer à travailler — sur l'écran dont
        // l'ergonomie a un coût salarial direct. Le dossier retenu est le
        // premier de la file, que le serveur a déjà ordonné par priorité puis
        // par ancienneté : c'est celui qu'il faut traiter, et le choisir ici
        // serait refaire un tri qu'on a déjà.
        openFirstByDefault
        renderDetail={(entree) => (
          <ReviewDetail
            entry={entree}
            view={dossier}
            reason={motif}
            onOpen={ouvrir}
            onPickReason={setMotif}
            rotation={rotation}
            zoom={zoom}
            contrast={contraste}
            onRotate={() => setRotation((tour) => (tour + 90) % 360)}
            onZoomIn={() => setZoom((facteur) => Math.min(3, facteur + 0.25))}
            onZoomOut={() => setZoom((facteur) => Math.max(1, facteur - 0.25))}
            // Le contraste TOURNE au lieu de se borner : une seule touche suffit
            // alors à parcourir les paliers et à revenir à l'image d'origine.
            // Un agent qui doit lire un timbre sec sous une photo surexposée ne
            // veut pas chercher un second bouton pour redescendre.
            onContrast={() =>
              setContraste((niveau) => (niveau >= 2 ? 1 : Math.round(niveau * 100 + 25) / 100))
            }
          />
        )}
      />
    </section>
  );
}

/** Ce que le panneau de détail attend. */
interface ReviewDetailProps {
  readonly entry: QueueEntry;
  readonly view: KycCaseView | null;
  readonly reason: string | null;
  readonly onOpen: (caseId: string) => void;
  readonly onPickReason: (code: string) => void;
  readonly rotation: number;
  readonly zoom: number;
  readonly contrast: number;
  readonly onRotate: () => void;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onContrast: () => void;
}

/**
 * Le dossier ouvert : pièce et selfie **côte à côte**, geste rappelé, métadonnées,
 * comptes rapprochés, et les motifs.
 *
 * Le chargement est déclenché par un effet plutôt que par le gestionnaire de
 * clic : la coquille ouvre aussi au clavier, et un chargement accroché au clic
 * aurait laissé l'ouverture par Entrée sans données.
 */
function ReviewDetail({
  entry,
  view,
  reason,
  onOpen,
  onPickReason,
  rotation,
  zoom,
  contrast,
  onRotate,
  onZoomIn,
  onZoomOut,
  onContrast,
}: ReviewDetailProps): ReactNode {
  const { t } = useTranslation();
  const charge = view !== null && view.row.caseId === entry.id;

  useEffect(() => {
    if (!charge) onOpen(entry.id);
  }, [charge, entry.id, onOpen]);

  if (!charge) {
    return <p className="review__loading">{t('identityReview.loading')}</p>;
  }

  const piece = view.captures.find((capture) => capture.side !== 'SELFIE') ?? null;
  const selfie = view.captures.find((capture) => capture.side === 'SELFIE') ?? null;

  return (
    <div className="review__case">
      {/* **Deux colonnes, et le document tient la large.** L'artboard `b_revue`
          met la pièce au centre en `flex:1` et la décision dans une colonne
          fixe à droite (`width:308px;flex:none`). C'est l'INVERSE de ce qui
          était livré, et l'inversion n'est pas cosmétique : le sujet de cet
          écran est la pièce d'identité, pas la liste. Un dossier logé dans un
          panneau latéral rendait « PIÈCE D'IDENTITÉ » sur autant de lignes
          qu'il a de lettres. */}
      <div className="review__document">
        <div className="review__tools" role="toolbar" aria-label={t('identityReview.tools')}>
          <button type="button" className="review__tool" onClick={onZoomIn}>
            {t('identityReview.zoomIn')}
          </button>
          <button type="button" className="review__tool" onClick={onZoomOut}>
            {t('identityReview.zoomOut')}
          </button>
          <button type="button" className="review__tool" onClick={onRotate}>
            {t('identityReview.rotate')}
          </button>
          <button type="button" className="review__tool" onClick={onContrast}>
            {t('identityReview.contrast')}
          </button>
        </div>

        {/* Côte à côte : c'est la comparaison qui décide, et deux images qu'il faut
            faire défiler l'une vers l'autre ne se comparent pas. */}
        <div className="review__captures">
          <CaptureFrame
            capture={piece}
            titleKey="identityReview.documentSide"
            rotation={rotation}
            zoom={zoom}
            contrast={contrast}
          />
          <CaptureFrame
            capture={selfie}
            titleKey="identityReview.selfieSide"
            rotation={rotation}
            zoom={zoom}
            contrast={contrast}
          />
        </div>

        <p className="review__gesture" data-testid="review-gesture">
          {view.gestureInstruction === null
            ? t('identityReview.gestureMissing')
            : t('identityReview.gesture', { instruction: view.gestureInstruction })}
        </p>
      </div>

      <div className="review__decision">
        {/* « Déclaré / lu sur la pièce » — le titre du dessin, et sous lui ce que
            le serveur sert VRAIMENT.

            La maquette dessine six lignes qui **confrontent** une valeur déclarée
            par le client à celle lue sur la pièce, chacune avec son état —
            « Concordant », « À vérifier ». `CaseView` ne porte rien de tout cela :
            ni nom, ni date de naissance, ni lieu de naissance déclarés, ni état de
            concordance. La ligne qui fait le prix de l'écran — « Kaolack (déclaré :
            Kaolak) · À vérifier » — n'a donc aucune matière.

            **Elle n'est pas fabriquée.** Une table de confrontation inventée serait
            pire que pas de table : elle donnerait à lire une concordance que
            personne n'a calculée, et l'agent approuverait sur la foi d'un vert
            dessiné. Ce qui suit est lu sur la PIÈCE et sur le DOSSIER, sans aucun
            état, et l'écart est dit à l'écran, ligne du dessin par ligne. */}
        <h3 className="review__compare-title">{t('identityReview.compareHeading')}</h3>
        <p className="review__compare-notice" data-testid="review-compare-notice">
          {t('identityReview.compareNotServed')}
        </p>

        <dl className="review__facts">
          <dt>{t('identityReview.documentLabel')}</dt>
          <dd data-testid="review-document">
            {view.document === null
              ? t('identityReview.documentMissing')
              : `${view.document.type} · ${view.document.number}`}
          </dd>
          <dt>{t('identityReview.validityLabel')}</dt>
          <dd data-testid="review-validity">
            {view.document === null || view.document.expiresOn === null
              ? t('identityReview.validityMissing')
              : t('identityReview.validityValue', { date: view.document.expiresOn })}
          </dd>
          <dt>{t('identityReview.originLabel')}</dt>
          <dd data-testid="review-origin">
            {view.row.outletCode === null
              ? t('identityReview.origins.self')
              : t('identityReview.origins.assistedFull', {
                  outlet: view.row.outletCode,
                  operator: view.row.operatorRef ?? '',
                })}
          </dd>
          <dt>{t('identityReview.matchesLabel')}</dt>
          <dd data-testid="review-matches">
            {view.alerts.length === 0
              ? t('identityReview.noMatch')
              : view.alerts.map((alerte) => alerte.typeCode).join(' · ')}
          </dd>
          {/* **L'ancienneté EXACTE et le verdict**, que la ligne de 240 points
              arrondit. Les deux vivent ici parce qu'il y a la place de les
              écrire : « 115 min 1 s — vieillissant » dans une file de 240 points
              y prenait celle de la référence du compte. */}
          <dt>{t('identityReview.ageLabel')}</dt>
          <dd data-testid="review-age">
            {view.row.ageing
              ? t('identityReview.ageingAge', { age: duree(view.row.ageSeconds) })
              : duree(view.row.ageSeconds)}
          </dd>
          {/* **L'affectation quitte la ligne et reste ici.** Le dessin met un
              état dans son étiquette, pas un identifiant d'analyste — et cet
              identifiant est un `UUID` de trente-six caractères, qui prenait
              quatre lignes dans une file de 240 points et rendait la file
              illisible à lui seul. Le retirer tout court aurait été la vraie
              perte : c'est lui qui dit qu'un dossier est déjà pris. */}
          <dt>{t('identityReview.assignmentLabel')}</dt>
          <dd data-testid="review-assignment">
            {view.row.assignedTo === null
              ? t('identityReview.unassigned')
              : t('identityReview.assignedTo', { analyst: view.row.assignedTo })}
          </dd>
          <dt>{t('identityReview.columns.alerts')}</dt>
          <dd data-testid="review-alert-count">
            {t('identityReview.alertCount', { count: view.row.alertCount })}
          </dd>
          {/* **La référence que le dessin écrit n'existe pas**, et l'écran le
              dit plutôt que de la fabriquer. L'en-tête porte « Aminata Diallo —
              3HXZLPC256 » : la seconde moitié est la référence du COMPTE, la
              seule qui soit servie. Sans cette ligne, un analyste lirait cette
              référence comme celle du dossier, la dicterait au téléphone, et
              personne ne la retrouverait. */}
          <dt>{t('identityReview.caseReferenceLabel')}</dt>
          <dd data-testid="review-case-reference">
            {t('identityReview.caseReferenceNotServed')}
          </dd>
        </dl>

        {/* Les motifs, groupés par décision. Le libellé est le texte que le CLIENT
            recevra : l'analyste choisit ce qui va partir, pas un code. */}
        <div className="review__reasons" role="group" aria-label={t('identityReview.reasons')}>
          {(
            [
              ['approve', view.approvalReasons],
              ['complement', view.informationReasons],
              ['reject', view.rejectionReasons],
            ] as const
          ).map(([groupe, motifs]) => (
            <fieldset className="review__reason-group" key={groupe}>
              <legend>{t(`identityReview.reasonGroups.${groupe}`)}</legend>
              {motifs.map((motif) => (
                <button
                  key={motif.code}
                  type="button"
                  className="review__reason"
                  data-reason={motif.code}
                  aria-pressed={reason === motif.code}
                  onClick={() => onPickReason(motif.code)}
                >
                  {motif.text}
                </button>
              ))}
            </fieldset>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Ce qu'un cadre sait de son image : rien encore, les octets, ou un refus nommé.
 *
 * **Il n'y a pas d'état « cassée ».** Une icône d'image brisée dit « quelque
 * chose a échoué » ; elle ne dit pas quoi, et surtout pas que l'image n'a jamais
 * été servie. Sur un écran où l'on juge une pièce d'identité, la différence
 * décide : un analyste qui croit voir un dossier incomplet le refuse, alors que
 * c'est le lien d'accès qui a expiré.
 */
type EtatDeLImage =
  | { readonly etat: 'chargement' }
  | { readonly etat: 'servie'; readonly url: string }
  | { readonly etat: 'refusee'; readonly cle: string };

/**
 * Les octets de la capture, lus par le **client HTTP** et non par le navigateur.
 *
 * Un `<img src>` n'emporte aucun en-tête : le serveur ne saurait pas *qui*
 * regarde, et ne pourrait donc pas inscrire au journal d'audit qu'un analyste a
 * vu la pièce (`EX-BO-004`, `EX-DAT-007`). L'image passe donc par le même
 * transport que le reste — jeton porteur, renouvellement de session et motif
 * d'audit compris —, puis devient une URL d'objet.
 */
function useImageDeCapture(capture: KycCapture | null): EtatDeLImage {
  const client = useApiClient();
  const [etat, setEtat] = useState<EtatDeLImage>({ etat: 'chargement' });

  useEffect(() => {
    if (capture === null) return undefined;
    let vivant = true;
    let objet: string | null = null;
    setEtat({ etat: 'chargement' });

    void (async () => {
      try {
        const octets = await fetchKycImage(
          client,
          capture.signedUrl,
          MOTIF_DE_CONSULTATION,
        );
        if (!vivant) return;
        // Le serveur reconnaît le format aux octets EUX-MÊMES, et rend
        // `application/octet-stream` quand il n'en reconnaît aucun. Le poser
        // dans un `src` afficherait une icône cassée ; on le DIT.
        if (!octets.contentType.startsWith('image/')) {
          setEtat({ etat: 'refusee', cle: 'identityReview.captureUnreadable' });
          return;
        }
        objet = URL.createObjectURL(octets.blob);
        setEtat({ etat: 'servie', url: objet });
      } catch (echec) {
        if (!vivant) return;
        // Les quatre refus de la route — pas de signature, signature falsifiée,
        // signature périmée, référence inconnue — sont rendus INDISTINGUABLES à
        // dessein. L'écran ne prétend donc pas dire laquelle des quatre causes
        // s'applique : il dit que le lien n'ouvre plus, et le geste qui le
        // renouvelle.
        const refus =
          echec instanceof ApiError &&
          (echec.kind === 'forbidden' || echec.status === 400 || echec.status === 403);
        setEtat({
          etat: 'refusee',
          cle: refus
            ? 'identityReview.captureRefused'
            : 'identityReview.captureUnavailable',
        });
      }
    })();

    return () => {
      vivant = false;
      // **La révocation n'est pas une hygiène de ressource.** Une URL d'objet
      // retient les octets de la pièce d'identité en mémoire tant qu'elle
      // existe, et reste ouvrable par quiconque la possède. La révoquer à la
      // fermeture est ce qui fait que la pièce ne survit pas au dossier.
      if (objet !== null) URL.revokeObjectURL(objet);
    };
  }, [capture, client]);

  return etat;
}

/** Ce que le cadre écrit quand il n'a pas d'image à montrer. */
function cleDeLEtat(image: EtatDeLImage, renduEchoue: boolean): string {
  if (renduEchoue) return 'identityReview.captureUnreadable';
  if (image.etat === 'chargement') return 'identityReview.captureLoading';
  if (image.etat === 'refusee') return image.cle;
  // « Servie » sans échec de rendu n'atteint jamais cette ligne : l'appelant a
  // déjà rendu l'image. Le cas est NOMMÉ plutôt que tu — rendre une chaîne vide
  // par défaut ferait afficher un cadre muet le jour où un quatrième état
  // apparaîtra, et un cadre muet ressemble à une image absente.
  return 'identityReview.captureUnreadable';
}

/** Un cadre d'image, avec sa transformation et ses métadonnées de capture. */
function CaptureFrame({
  capture,
  titleKey,
  rotation,
  zoom,
  contrast,
}: {
  readonly capture: KycCapture | null;
  readonly titleKey: string;
  readonly rotation: number;
  readonly zoom: number;
  readonly contrast: number;
}): ReactNode {
  const { t } = useTranslation();
  const image = useImageDeCapture(capture);
  // Le dernier filet : des octets acceptés par le serveur que le navigateur ne
  // sait pourtant pas peindre. Sans lui, ce cas SEUL rendrait l'icône cassée que
  // tout le reste de ce composant existe pour éviter.
  const [renduEchoue, setRenduEchoue] = useState(false);
  useEffect(() => setRenduEchoue(false), [capture]);

  if (capture === null) {
    return (
      <figure className="review__capture">
        <figcaption>{t(titleKey)}</figcaption>
        <p className="review__capture-missing">{t('identityReview.captureMissing')}</p>
      </figure>
    );
  }

  return (
    <figure className="review__capture" data-side={capture.side}>
      <figcaption>{t(titleKey)}</figcaption>
      {image.etat === 'servie' && !renduEchoue ? (
        /* L'accès passe par une URL SIGNÉE de courte durée : il n'y a pas
           d'autre chemin vers une image de dossier (EX-DAT-006). Les octets
           sont ici déjà en main, et `src` porte l'URL d'objet qui les rend. */
        <img
          className="review__image"
          src={image.url}
          alt={t('identityReview.captureAlt', { side: capture.side })}
          onError={() => setRenduEchoue(true)}
          // Les quatre outils agissent SUR L'IMAGE, et non sur une vignette
          // décorative : c'est ce qui permet de juger une pièce photographiée de
          // travers dans une échoppe mal éclairée, plutôt que d'approuver par
          // lassitude.
          style={{
            transform: `rotate(${rotation}deg) scale(${zoom})`,
            filter: `contrast(${contrast})`,
          }}
        />
      ) : (
        <p
          className="review__capture-missing"
          data-testid={`review-image-state-${capture.side}`}
          role={image.etat === 'chargement' && !renduEchoue ? undefined : 'alert'}
        >
          {t(cleDeLEtat(image, renduEchoue))}
        </p>
      )}
      <p className="review__metadata" data-testid={`review-metadata-${capture.side}`}>
        {t('identityReview.metadata', {
          source: capture.source,
          capturedAt: capture.capturedAt,
          device: capture.deviceFingerprint,
        })}
      </p>
    </figure>
  );
}
